import {
  CommandLineIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  QueueListIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { getSalesProduct, listSalesProducts, SalesProduct, SalesProductSummary } from '../../services/salesProductsApi';
import {
  TechnologyApplication,
  getTechnologyApplication,
  listTechnologyApplications,
} from '../../services/technologyApi';
import {
  TechnologyProductApplicationLink,
  createTechnologyProductApplicationLink,
  deleteTechnologyProductApplicationLink,
  listTechnologyProductApplicationLinks,
} from '../../services/technologyDeploymentsApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { TechnologyApplicationInventoryModal } from './TechnologyApplicationInventoryModal';

const PRODUCT_PAGE_SIZE = 200;

const AVATAR_COLORS = [
  'bg-sky-100 text-sky-700',
  'bg-indigo-100 text-indigo-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-violet-100 text-violet-700',
  'bg-rose-100 text-rose-700',
];

function plainText(html: string | null | undefined): string {
  if (!html) return '';
  const document = new DOMParser().parseFromString(html, 'text/html');
  return (document.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

function avatarColor(name: string): string {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = (hash + name.charCodeAt(index)) % AVATAR_COLORS.length;
  }
  return AVATAR_COLORS[hash] ?? AVATAR_COLORS[0];
}

function ProductMark({ product }: { product: SalesProductSummary }) {
  if (product.logoBase64) {
    return (
      <img
        src={product.logoBase64}
        alt=""
        className="h-10 w-10 shrink-0 rounded-xl object-contain"
      />
    );
  }

  const initial = product.name.trim().charAt(0).toUpperCase() || 'P';
  return (
    <span
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-semibold ${avatarColor(product.name)}`}
    >
      {initial}
    </span>
  );
}

export function TechnologyPortfolioPage() {
  const navigate = useNavigate();
  const { confirm } = useConfirmDialog();
  const { canWriteModule, canAccessModule, canReadModule } = usePermissions();
  const canWrite = canWriteModule('technology.portfolio');
  const canOpenInventory = canReadModule('technology.applications');
  const canOpenOverview = canAccessModule('technology.overview');

  const [products, setProducts] = useState<SalesProductSummary[]>([]);
  const [productDetail, setProductDetail] = useState<SalesProduct | null>(null);
  const [links, setLinks] = useState<TechnologyProductApplicationLink[]>([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [productQuery, setProductQuery] = useState('');
  const [applicationQuery, setApplicationQuery] = useState('');
  const [searchResults, setSearchResults] = useState<TechnologyApplication[]>([]);
  const [searching, setSearching] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [busy, setBusy] = useState(false);
  const [inventoryApplication, setInventoryApplication] = useState<TechnologyApplication | null>(null);

  const selectedProduct = products.find((product) => product.id === selectedProductId) ?? null;

  const linksByProduct = useMemo(() => {
    const map = new Map<string, TechnologyProductApplicationLink[]>();
    for (const link of links) {
      const current = map.get(link.salesProductId) ?? [];
      current.push(link);
      map.set(link.salesProductId, current);
    }
    map.forEach((productLinks) => {
      productLinks.sort((left, right) =>
        (left.applicationName ?? '').localeCompare(right.applicationName ?? '', 'pt-BR'),
      );
    });
    return map;
  }, [links]);

  const selectedLinks = useMemo(
    () => linksByProduct.get(selectedProductId) ?? [],
    [linksByProduct, selectedProductId],
  );
  const linkedApplicationIds = useMemo(
    () => new Set(selectedLinks.map((link) => link.applicationId)),
    [selectedLinks],
  );

  const visibleProducts = useMemo(() => {
    const query = productQuery.trim().toLocaleLowerCase('pt-BR');
    if (!query) return products;
    return products.filter((product) => {
      const pitch = (product.elevatorPitch ?? '').toLocaleLowerCase('pt-BR');
      return product.name.toLocaleLowerCase('pt-BR').includes(query) || pitch.includes(query);
    });
  }, [products, productQuery]);

  const pitch = plainText(productDetail?.elevatorPitchHtml) || selectedProduct?.elevatorPitch || '';
  const description = plainText(productDetail?.descriptionHtml);
  const productTooltip = [pitch, description && description !== pitch ? description : '']
    .filter((part) => part.length > 0)
    .join('\n');

  async function loadProducts(): Promise<void> {
    setLoadingProducts(true);
    try {
      const result = await listSalesProducts({ activeOnly: true, page: 1, pageSize: PRODUCT_PAGE_SIZE });
      const items = result.items ?? [];
      setProducts(items);
      setSelectedProductId((current) => current || items[0]?.id || '');
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar produtos.'));
    } finally {
      setLoadingProducts(false);
    }
  }

  async function loadLinks(): Promise<void> {
    try {
      setLinks(await listTechnologyProductApplicationLinks());
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar a composição.'));
    }
  }

  useEffect(() => {
    void loadProducts();
    void loadLinks();
  }, []);

  useEffect(() => {
    if (!selectedProductId) {
      setProductDetail(null);
      return;
    }
    let cancelled = false;
    void getSalesProduct(selectedProductId)
      .then((product) => {
        if (!cancelled) setProductDetail(product);
      })
      .catch(() => {
        if (!cancelled) setProductDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProductId]);

  useEffect(() => {
    const query = applicationQuery.trim();
    if (query.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    let cancelled = false;
    setSearching(true);
    const timer = window.setTimeout(() => {
      void listTechnologyApplications({ search: query, page: 1, pageSize: 8 })
        .then((result) => {
          if (cancelled) return;
          setSearchResults((result.items ?? []).filter((item) => !linkedApplicationIds.has(item.id)));
        })
        .catch((error) => {
          if (!cancelled) {
            setSearchResults([]);
            toast.error(getRequestErrorMessage(error, 'Falha ao buscar aplicações.'));
          }
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [applicationQuery, linkedApplicationIds]);

  async function addApplication(applicationId: string): Promise<void> {
    if (!canWrite || !selectedProductId || busy) return;
    setBusy(true);
    try {
      await createTechnologyProductApplicationLink({
        salesProductId: selectedProductId,
        applicationId,
      });
      await loadLinks();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao incluir a aplicação.'));
    } finally {
      setBusy(false);
    }
  }

  async function removeLink(link: TechnologyProductApplicationLink): Promise<void> {
    if (!canWrite || busy) return;
    const accepted = await confirm({
      title: 'Remover da composição',
      message: `Remover "${link.applicationName ?? 'esta aplicação'}" de ${selectedProduct?.name ?? 'este produto'}? A aplicação continua cadastrada.`,
      confirmLabel: 'Remover',
      tone: 'danger',
    });
    if (!accepted) return;

    setBusy(true);
    try {
      await deleteTechnologyProductApplicationLink(link.id);
      await loadLinks();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao remover a aplicação.'));
    } finally {
      setBusy(false);
    }
  }

  async function openInventory(applicationId: string): Promise<void> {
    try {
      setInventoryApplication(await getTechnologyApplication(applicationId));
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao abrir o inventário da aplicação.'));
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Composição dos sistemas"
        description="Quais aplicações, APIs e serviços compõem cada produto. Esse vínculo alimenta o filtro por produto em Deploys."
        actions={
          canOpenOverview ? (
            <button
              type="button"
              className="erp-btn-secondary gap-2"
              onClick={() =>
                navigate(
                  selectedProductId
                    ? `/technology/overview?salesProductId=${selectedProductId}`
                    : '/technology/overview',
                )
              }
            >
              <MapPinIcon className="h-4 w-4" aria-hidden="true" />
              Ver no mapa de infraestrutura
            </button>
          ) : null
        }
      />

      {!loadingProducts && products.length === 0 ? (
        <p className="text-sm text-secondary-500">
          Cadastre um produto para definir as aplicações que o compõem.
        </p>
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
          <section className="rounded-2xl border border-secondary-200 bg-white shadow-sm">
            <div className="border-b border-secondary-100 px-4 py-3">
              <h2 className="text-sm font-semibold text-secondary-900">Produtos</h2>
              <label className="relative mt-3 block">
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400"
                  aria-hidden="true"
                />
                <input
                  className="erp-input w-full pl-9"
                  placeholder="Buscar produto..."
                  value={productQuery}
                  onChange={(event) => setProductQuery(event.target.value)}
                />
              </label>
            </div>
            {loadingProducts ? (
              <div className="p-4">
                <LoadingSpinner label="Carregando produtos..." />
              </div>
            ) : visibleProducts.length === 0 ? (
              <p className="px-4 py-6 text-sm text-secondary-500">Nenhum produto encontrado.</p>
            ) : (
              <ul className="max-h-[40rem] divide-y divide-secondary-100 overflow-y-auto">
                {visibleProducts.map((product) => {
                  const count = linksByProduct.get(product.id)?.length ?? 0;
                  const active = product.id === selectedProductId;
                  return (
                    <li key={product.id}>
                      <button
                        type="button"
                        className={`flex w-full items-center gap-3 px-3 py-3 text-left transition-colors ${
                          active ? 'bg-sky-50' : 'hover:bg-secondary-50'
                        }`}
                        title={product.elevatorPitch || undefined}
                        onClick={() => setSelectedProductId(product.id)}
                      >
                        <ProductMark product={product} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-secondary-900">
                            {product.name}
                          </span>
                        </span>
                        <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-secondary-100 px-1.5 text-xs font-semibold text-secondary-600">
                          {count}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="space-y-4">
            {!selectedProduct ? (
              <p className="text-sm text-secondary-500">Selecione um produto.</p>
            ) : (
              <>
                <article className="flex items-start gap-3 rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
                  <ProductMark product={selectedProduct} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2
                        className="text-lg font-semibold text-secondary-900"
                        title={productTooltip || undefined}
                      >
                        {selectedProduct.name}
                      </h2>
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                        Ativo
                      </span>
                    </div>
                  </div>
                </article>

                {canWrite ? (
                  <article className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
                    <h3 className="text-sm font-semibold text-secondary-900">Adicionar aplicação</h3>
                    <p className="mt-1 text-sm text-secondary-500">
                      Busque e selecione as aplicações que fazem parte deste produto.
                    </p>
                    <label className="relative mt-3 block">
                      <MagnifyingGlassIcon
                        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400"
                        aria-hidden="true"
                      />
                      <input
                        className="erp-input w-full pl-9 pr-9"
                        placeholder="Buscar aplicação..."
                        value={applicationQuery}
                        disabled={busy}
                        onChange={(event) => setApplicationQuery(event.target.value)}
                      />
                      {applicationQuery ? (
                        <button
                          type="button"
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-700"
                          aria-label="Limpar busca"
                          onClick={() => setApplicationQuery('')}
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      ) : null}
                    </label>
                    {applicationQuery.trim().length >= 2 ? (
                      <ul className="mt-2 divide-y divide-secondary-100 overflow-hidden rounded-xl border border-secondary-200">
                        {searching ? (
                          <li className="px-3 py-3 text-sm text-secondary-500">Buscando...</li>
                        ) : searchResults.length === 0 ? (
                          <li className="px-3 py-3 text-sm text-secondary-500">
                            Nenhuma aplicação disponível para esta busca.
                          </li>
                        ) : (
                          searchResults.map((application) => (
                            <li key={application.id}>
                              <button
                                type="button"
                                className="block w-full truncate px-3 py-2.5 text-left text-sm font-medium text-secondary-900 hover:bg-secondary-50 disabled:opacity-60"
                                disabled={busy}
                                onClick={() => void addApplication(application.id)}
                              >
                                {application.name}
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    ) : null}
                  </article>
                ) : null}

                <article className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
                  <h3 className="text-sm font-semibold text-secondary-900">
                    Aplicações selecionadas ({selectedLinks.length})
                  </h3>
                  <p className="mt-1 text-sm text-secondary-500">
                    Aplicações que compõem o produto {selectedProduct.name}.
                  </p>

                  {selectedLinks.length === 0 ? (
                    <p className="mt-4 text-sm text-secondary-500">
                      Nenhuma aplicação nesta composição.
                    </p>
                  ) : (
                    <ul className="mt-3 divide-y divide-secondary-100 overflow-hidden rounded-xl border border-secondary-200">
                      {selectedLinks.map((link) => (
                        <li key={link.id} className="flex items-center gap-3 bg-white px-3 py-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                            <CommandLineIcon className="h-4 w-4" aria-hidden="true" />
                          </span>
                          <span className="min-w-0 flex-1" title={link.applicationNotes || undefined}>
                            <span className="block truncate text-sm font-medium text-secondary-900">
                              {link.applicationName}
                            </span>
                          </span>
                          <span className="flex shrink-0 items-center gap-1">
                            {canOpenInventory ? (
                              <button
                                type="button"
                                className="rounded-md p-1.5 text-secondary-500 hover:bg-primary-50 hover:text-primary-600"
                                title="Inventário"
                                aria-label="Inventário"
                                onClick={() => void openInventory(link.applicationId)}
                              >
                                <QueueListIcon className="h-4 w-4" />
                              </button>
                            ) : null}
                            {canWrite ? (
                              <button
                                type="button"
                                className="rounded-md p-1.5 text-secondary-500 hover:bg-rose-50 hover:text-rose-600"
                                title="Remover da composição"
                                aria-label="Remover da composição"
                                disabled={busy}
                                onClick={() => void removeLink(link)}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            ) : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              </>
            )}
          </section>
        </div>
      )}

      {inventoryApplication ? (
        <TechnologyApplicationInventoryModal
          open
          application={inventoryApplication}
          canWrite={canWriteModule('technology.applications')}
          onClose={() => setInventoryApplication(null)}
          onSaved={() => loadLinks()}
        />
      ) : null}
    </PageShell>
  );
}
